import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProofTile from './ProofTile';

const ProofGrid = ({
  proofs,
  onDeleteProof,
  onEditProof,
  onOpenProofDetail,
  onOpenProofUpload,
  onReportProof,
  canEdit = true,
}) => {
  const containerRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const [layout, setLayout] = useState({ cols: 2, gapPx: 12, rowHeight: 240 });
  const [range, setRange] = useState({ startRow: 0, endRow: 0 });

  const nextIndex = useMemo(
    () =>
      proofs?.findIndex(
        (p, idx) => !p?.completed && (idx === 0 || proofs?.[idx - 1]?.completed)
      ),
    [proofs]
  );

  useEffect(() => {
    const getCols = (w) => {
      if (w >= 1280) return 5;
      if (w >= 1024) return 4;
      if (w >= 768) return 3;
      return 2;
    };

    const getGapPx = (w) => {
      if (w >= 1024) return 24;
      if (w >= 768) return 16;
      return 12;
    };

    const updateLayout = () => {
      const el = containerRef.current;
      if (!el) return;

      const w = window.innerWidth;
      const cols = getCols(w);
      const gapPx = getGapPx(w);

      const containerWidth = el.clientWidth;
      const tileSize = Math.max(
        160,
        Math.floor((containerWidth - gapPx * (cols - 1)) / cols)
      );
      const rowHeight = tileSize + gapPx;

      setLayout({ cols, gapPx, rowHeight });
    };

    updateLayout();

    const ro = new ResizeObserver(() => updateLayout());
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', updateLayout);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateLayout);
    };
  }, []);

  useEffect(() => {
    const updateRange = () => {
      const el = containerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportTop = 0;
      const viewportBottom = window.innerHeight;

      const visibleTop = Math.max(rect.top, viewportTop);
      const visibleBottom = Math.min(rect.bottom, viewportBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      const scrollTopInGrid = Math.max(0, viewportTop - rect.top);

      const overscanRows = 2;
      const startRow = Math.max(
        0,
        Math.floor(scrollTopInGrid / layout.rowHeight) - overscanRows
      );
      const endRow = Math.max(
        startRow,
        Math.ceil((scrollTopInGrid + visibleHeight) / layout.rowHeight) +
          overscanRows
      );

      setRange({ startRow, endRow });
    };

    updateRange();
    window.addEventListener('scroll', updateRange, { passive: true });
    window.addEventListener('resize', updateRange);

    return () => {
      window.removeEventListener('scroll', updateRange);
      window.removeEventListener('resize', updateRange);
    };
  }, [layout.rowHeight]);

  useEffect(() => {
    if (hasAutoScrolledRef?.current) return;
    if (nextIndex == null || nextIndex < 0) return;

    const el = containerRef.current;
    if (!el) return;

    const targetRow = Math.floor(nextIndex / layout.cols);
    const rect = el.getBoundingClientRect();
    const gridTopPageY = window.scrollY + rect.top;
    const targetY =
      gridTopPageY +
      targetRow * layout.rowHeight -
      window.innerHeight / 2 +
      layout.rowHeight / 2;

    window.scrollTo({ top: Math.max(0, targetY), behavior: 'auto' });
    hasAutoScrolledRef.current = true;
  }, [layout.cols, layout.rowHeight, nextIndex]);

  const getProofStatus = (index) => {
    const proof = proofs?.[index];
    
    if (proof?.completed) {
      return { type: 'completed', proof };
    }
    
    if (index === 0 || proofs?.[index - 1]?.completed) {
      return { type: 'next', proof };
    }
    
    return { type: 'locked', proof };
  };

  const totalRows = Math.ceil((proofs?.length || 0) / layout.cols);
  const clampedEndRow = Math.min(totalRows, range.endRow);
  const visibleRows = Math.max(0, clampedEndRow - range.startRow);
  const startIndex = range.startRow * layout.cols;
  const endIndex = Math.min((range.startRow + visibleRows) * layout.cols, proofs?.length || 0);

  const paddingTop = range.startRow * layout.rowHeight;
  const paddingBottom = Math.max(0, (totalRows - clampedEndRow) * layout.rowHeight);

  return (
    <div ref={containerRef}>
      <div style={{ height: paddingTop }} />
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6"
      >
        {proofs?.slice(startIndex, endIndex)?.map((proof, i) => {
          const index = startIndex + i;
          const status = getProofStatus(index);
          const isNext = status?.type === 'next';
          const isLocked = status?.type === 'locked';

          return (
            <div key={proof?.id}>
              <ProofTile
                proof={proof}
                isLocked={isLocked}
                isNext={isNext}
                canEdit={canEdit}
                onDelete={onDeleteProof}
                onEdit={onEditProof}
                onOpenDetail={onOpenProofDetail}
                onOpenUpload={onOpenProofUpload}
                onReport={onReportProof}
              />
            </div>
          );
        })}
      </div>
      <div style={{ height: paddingBottom }} />
    </div>
  );
};

export default ProofGrid;